import { FC, useState } from 'react';

import { Col, message, ModalProps, Row, Spin, UploadFile, UploadProps } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import { Modal } from '@hdi-seguros/web-components.modal';

import ProducersService from '../../../../../shared/services/producers';

import { InsurancePolicy, ProducerContext } from '../../../../../shared/interfaces';
import userHelper from '../../../../../shared/utils/userHelper';

import './index.css';
import { DraggerUploadFile } from '../../../../../shared/components';
import { ActionButton } from '@hdi-seguros/web-components.action-button';
import dayjs from 'dayjs';


interface Props extends ModalProps {
  insurancePolicy?: InsurancePolicy;
  onClose: () => void;
  pendingInvoiceSelected: any;
  selectedProducer?: ProducerContext;
  onSuccess: () => void;
}

export const UploadFileModal: FC<Props> = ({
  open,
  title,
  onClose,
  onSuccess,
  pendingInvoiceSelected,
  selectedProducer,
}) => {
  const [fileError, setFileError] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [fileSelect, setFileSelect] = useState<any>();

  const openFile = () => {
    let messageError: string = '';

    if (fileSelect.base) {
      const newWindow = window.open();

      if (newWindow) {
        const document: string = `<iframe src="${fileSelect.base}" style="width:100%; height:100%;"></iframe>`;
        newWindow.document.write(document);

        return;
      }

      messageError =
        'La ventana emergente no se pudo abrir. Asegúrate de que las ventanas emergentes no estén bloqueadas en tu navegador.';
      message.error(messageError);

      return;
    }

    messageError = 'No hay archivo seleccionado o el archivo está vacío.';
    message.error(messageError);
  };

  const buildNewFilename = (fileSelect: any) => {
    const userInfo = userHelper.getUser();
    const intermediaryCuit =
      selectedProducer?.cuitInter ||
      userInfo?.intermediarioSecundario?.cuitInter ||
      '';

    const currentDate = dayjs().format('DD.MM.YYYY_HH:mm:ss');

    const filenameParts = fileSelect.filename.split('.');
    const fileExtension = filenameParts.pop() || '';
    const newFilename = `FACTURA_COMISIONES_${intermediaryCuit}_${currentDate}.${fileExtension}`;

    return newFilename;
  };


  const postReportDocumentEntry = async (newFilename :string) => {
    try {
      const args = {
        ...fileSelect,
        filename: newFilename,
        sequence: pendingInvoiceSelected?.secuencia,
        dateInvoice: pendingInvoiceSelected?.fechaFactura,
        selectedProducer,
      };

      await ProducersService.postReportDocumentEntry(args);
      onSuccess()
      setFileSelect(null);
    } catch (error) {      
      setFileError(true);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const fileToBase64 = (file: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (event) => {
        const errorMessage = `Error al cargar el archivo: ${event?.target?.error?.message}`;
        reject(new Error(errorMessage));
      };
    });
  };

  const validate = (): boolean | void => {
    if (fileSelect) return true;

    const messageError = 'No se ha seleccionado ningún archivo.';
    message.error(messageError);
  };

  const handleUpload = (): void => {
    if (validate()) postUploadInvoice();
  };

  const postUploadInvoice = async () => {
    try {
      setLoading(true);
      const newFilename = buildNewFilename(fileSelect);
      const args = {
        ...fileSelect,
        filename: newFilename,
        sequence: pendingInvoiceSelected?.secuencia,
        dateInvoice: pendingInvoiceSelected?.fechaFactura,
        selectedProducer,
      };
  
      await ProducersService.postUploadInvoice(args);
      await postReportDocumentEntry(newFilename);
    } catch (error) {
      setFileError(true);
      setLoading(false);
    }
  };

  const handleFileSelection = async (
    info: UploadChangeParam<UploadFile<any>>
  ) => {
    setFileError(false);
    const removed: string = 'removed';

    if (info.file.status === removed) {
      setFileSelect(null);
      return;
    }

    const files = info.fileList.map((file) => {
      return {
        ...file,
        name: file.name || fileSelect.name,
        base64: fileToBase64(file.originFileObj),
      };
    });

    const base = await files[0].base64;
    const mediaType = files[0].type;
    let filename = files[0].name;

    const filenameParts = filename.split(".");
    const filenameExtension = filenameParts.pop();
    const filenameUpper = filenameParts.join("."); 

    filename = filenameUpper.toUpperCase() + "." + filenameExtension;
    
    setFileSelect({ mediaType, filename, base, name: filename });
  };

  const handleFileDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    setFileError(false);

    const file = e.dataTransfer.files[0];

    if (file) {
      try {
        const base64 = await fileToBase64(file);
        const base = await base64;
        const mediaType = file.type;
        const filename = file.name;

        setFileSelect({ mediaType, filename, base, name: filename });
      } catch (error) {
        message.error('Error al cargar el archivo');
      }
    }
  };

  const draggerProps: UploadProps = {
    name: 'file',
    multiple: false,
    maxCount: 1,
    accept: 'file/pdf',
    customRequest: () => false,
    onChange: (info) => {
      handleFileSelection(info);
    },
    onDrop(e) {
      handleFileDrop(e);
    },
  };

  const handleCancel = () => {
    if (loading) setFileSelect(null);

    onClose();
  };

  const renderContent = () => (
    <Modal
      title={title}
      centered
      open={open}
      width={650}
      onCancel={handleCancel}
      loadingConfirmButton={loading}
      footer={
        <>
          <ActionButton
            label='Cancelar'
            type="secondary"
            className='btn-accent-outline-barbuss '
            onClick={handleCancel}
          />
          <ActionButton 
            className='btn-principal-barbuss '
            label='Confirmar'
            type="primary"
            onClick={handleUpload}
            disabled={!fileSelect || loading}
          />
        </>
      }
    >
      <Spin 
        spinning={loading} 
        tip="Subiendo archivo por favor espere..."
        size="large"
        style={{ minHeight: '200px' }}
      >
        <Row>
          <Col span={24} style={{ marginBottom: 20, marginTop: 10 }}>
            <Row justify='center'>
              <DraggerUploadFile 
                draggerProps={draggerProps}
                fileList={fileSelect ? [fileSelect] : []}
                onPreview={openFile}
                formatFile={'PDF'}
                className={fileError ? 'error-dragger' : undefined}
              />
            </Row>
          </Col>
        </Row>
      </Spin>
    </Modal>
  );

  return <>{renderContent()}</>;
};
